import { invariantResponse } from '@epic-web/invariant'
import { json } from '@remix-run/node'
import { Link, Outlet, useLoaderData, useLocation } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import Footer from '#app/components/footer.tsx'
import General from '#app/components/navs/general.tsx'
import ModernNav from '#app/components/navs/modern-nav.tsx'
import PlayerNav from '#app/components/navs/player-nav.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { useOptionalUser } from '#app/utils/user.ts'

export const loader = async () => {
	const players = await prisma.player.findMany({
		select: {
			id: true,
		},
	})

	invariantResponse(players, 'No players found', { status: 404 })
	return json({ players })
}

const landingPages = [
	'/',
	'/communities',
	'/competitions',
	'/about',
	'/rules',
	'/pricing',
	'/contact',
	'/blog',
]

const RootLayout = () => {
	const { players } = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const { pathname } = useLocation()

	const isOwner = players.some(
		(player) => player.id === user?.playerProfile?.id,
	)

	const isLandingPage = landingPages.includes(pathname)
	const isPlayerRoute = pathname.startsWith('/player/')
	const isRunClubRoute = pathname.startsWith('/run-club')

	// Determine which navigation component to use
	const getNavigation = () => {
		if (isPlayerRoute && user?.playerProfile?.id === pathname.split('/')[2] && isOwner) {
			return <PlayerNav isOwner={isOwner} />
		}
		return <General user={user} />
	}

	// Don't render this layout for run-club routes
	if (isRunClubRoute) {
		return <Outlet />
	}

	if (isLandingPage) {
		return (
			<div className="relative min-h-screen bg-background-dark">
				<ModernNav user={user} />
				<main className="flex flex-col">
					<Outlet />
				</main>
				<Footer />
			</div>
		)
	}

	return (
		<div className="relative min-h-screen bg-background-dark">
			<header className="fixed inset-x-0 top-0 z-50">
				<div className="relative">
					{/* Navigation content */}
					<nav className="relative mx-auto flex h-16 max-w-[1600px] items-center justify-between bg-background-dark/95 backdrop-blur-sm px-3 sm:px-4 lg:px-6">
						<Link 
							to="/" 
							className="relative flex items-center gap-2 transition-opacity hover:opacity-80"
						>
							<img 
								src="/img/logos/logo.png" 
								alt="7even Sports" 
								className="h-8 w-auto" 
							/>
						</Link>
						{getNavigation()}
					</nav>
				</div>
			</header>

			<main className="mx-auto min-h-[calc(100vh-5rem)] pt-16">
				<Outlet />
			</main>

			<Footer />
		</div>
	)
}

export default RootLayout

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
